// SupplierPage.js

import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import SupplierList from "common/Suppliers/SupplierList";
import ImportSuppliersButton from "common/Suppliers/ImportSuppliersButton";
import { Box, Fade, IconButton, Tooltip } from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MDButton from "components/MDButton";
import { useSnackbar } from "context/SnackbarContext";
import { useLanguage } from "context/LanguageContext";

const SupplierPage = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();

  const handleImportSuccess = (result) => {
    setRefreshTrigger((prev) => prev + 1);

    const created = result?.summary?.created || 0;
    setImportSummary({ created });

    setImportResult(result || null);
    setImportDialogOpen(true);

    const toastMessage = (t("createdCount") || "Created: {count}").replace(
      "{count}",
      String(created)
    );
    showSnackbar(toastMessage, "success");
  };

  const renderSupplierList = (title, items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
      <Box mt={2}>
        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
          {title} ({items.length})
        </MDTypography>
        <List
          dense
          sx={{
            py: 0,
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          {items.map((item, idx) => (
            <ListItem key={`${title}-${item?.supplier_registration || item?.name || idx}`} divider>
              <ListItemText
                primary={item?.name || "Unknown"}
                secondary={item?.supplier_registration || item?.reason || "-"}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshTrigger((prev) => prev + 1);
    setTimeout(() => {
      setIsRefreshing(false);
      showSnackbar(t("supplierListRefreshed"), "success");
    }, 500);
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {t("supplierManagement")}
                </MDTypography>
                <Box display="flex" gap={1}>
                  <Tooltip title={t("refreshList")}>
                    <IconButton
                      color="inherit"
                      onClick={handleRefresh}
                      sx={{
                        transform: isRefreshing ? "rotate(180deg)" : "none",
                        transition: "transform 0.5s ease-in-out",
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <ImportSuppliersButton onImportSuccess={handleImportSuccess}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon />
                      <MDTypography variant="button" color="white">
                        {t("importSuppliers")}
                      </MDTypography>
                    </Box>
                  </ImportSuppliersButton>
                </Box>
              </MDBox>

              <Fade in={!isRefreshing} timeout={500}>
                <Box>
                  <SupplierList refreshTrigger={refreshTrigger} />
                </Box>
              </Fade>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("importSuppliers")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text" mb={1}>
            {t("importCompleted") || "Import completed."}
          </MDTypography>

          <Alert severity="success" sx={{ mt: 1 }}>
            {(t("createdCount") || "Created: {count}").replace(
              "{count}",
              String(importResult?.summary?.created || 0)
            )}
          </Alert>

          {renderSupplierList(t("createdSuppliers") || "Created", importResult?.created_suppliers)}

          {(!Array.isArray(importResult?.created_suppliers) ||
            importResult.created_suppliers.length === 0) && (
            <MDTypography variant="caption" color="text" display="block" mt={2}>
              {t("noCreatedSuppliers") || "No new suppliers were created."}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={() => setImportDialogOpen(false)}>
            {t("close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default SupplierPage;
